import React from 'react';
import { CardContent, Typography, Box, Chip, List, ListItem, ListItemText, LinearProgress, Card, Alert, AlertTitle } from '@mui/material';
import { TrendingUp, TrendingDown, TrendingFlat, Warning, Timeline, Security, Api, Computer, BugReport, Speed } from '@mui/icons-material';
import { MLService } from '@/services/ml/MLService';
import { Incident, CatalogTask } from '@/types/dashboard';
import { format, differenceInDays, parseISO, isAfter, subDays, parse } from 'date-fns';

interface AIAnalysisProps {
  incidents: Incident[];
  catalogTasks: CatalogTask[];
  timePeriodDays?: number;
}

interface ApiFailurePrediction {
  apiName: string;
  pastFailures: number;
  recentFailures: number;
  likelihood: 'High' | 'Medium' | 'Low';
  lastIncident: string;
}

export const AIAnalysis: React.FC<AIAnalysisProps> = ({ incidents, catalogTasks, timePeriodDays = 30 }) => {
  const [analysis, setAnalysis] = React.useState<any>(null);
  const [apiFailures, setApiFailures] = React.useState<ApiFailurePrediction[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const analyzeData = async () => {
      try {
        console.log('AI Analysis - Time Period Days:', timePeriodDays);
        console.log('AI Analysis - Total incidents:', incidents.length);
        
        const mlService = MLService.getInstance();
        const results = await mlService.analyzeIncidents(incidents, catalogTasks, timePeriodDays);
        setAnalysis(results);

        // Generate simple API failure predictions
        const apiFailuresData = generateApiFailurePredictions(incidents);
        console.log('API Failures found:', apiFailuresData);
        setApiFailures(apiFailuresData);
      } catch (error) {
        console.error('Error analyzing data:', error);
      } finally {
        setLoading(false);
      }
    };

    analyzeData();
  }, [incidents, catalogTasks, timePeriodDays]);

  // Simple API Failure Prediction Function
  const generateApiFailurePredictions = (incidents: any[]): ApiFailurePrediction[] => {
    const apiFailureMap = new Map<string, any[]>();

    // Extract API names with specific extensions that have had incidents
    incidents.forEach((incident, index) => {
      const description = incident["Short description"] || '';
      const apiName = extractApiNameWithExtensions(description);
      
      if (apiName) {
        console.log(`Found API: ${apiName} in incident ${index + 1}: ${description.substring(0, 100)}...`);
        if (!apiFailureMap.has(apiName)) {
          apiFailureMap.set(apiName, []);
        }
        apiFailureMap.get(apiName)!.push(incident);
      }
    });

    console.log('Total unique APIs found:', apiFailureMap.size);

         // Calculate predictions for APIs that have had failures
     const thirtyDaysAgo = subDays(new Date(), 30);
     const predictions: ApiFailurePrediction[] = [];

     // Helper function to parse the custom date format
     const parseCustomDate = (dateString: string): Date => {
       try {
         // Try parsing the format "MM/dd/yyyy hh:mm:ss AM/PM"
         return parse(dateString, 'MM/dd/yyyy hh:mm:ss a', new Date());
       } catch (error) {
         console.warn('Date parsing error for:', dateString);
         return new Date();
       }
     };

     apiFailureMap.forEach((failureIncidents, apiName) => {
       const pastFailures = failureIncidents.length;
       
       const recentFailures = failureIncidents.filter(inc => {
         const date = parseCustomDate(inc.Opened);
         return isAfter(date, thirtyDaysAgo);
       }).length;

       // Simple likelihood calculation
       let likelihood: 'High' | 'Medium' | 'Low';
       if (recentFailures >= 3) likelihood = 'High';
       else if (recentFailures >= 1) likelihood = 'Medium';
       else if (pastFailures >= 3) likelihood = 'Medium';
       else likelihood = 'Low';

       // Get last incident date
       const sortedIncidents = failureIncidents.sort((a, b) => 
         parseCustomDate(b.Opened).getTime() - parseCustomDate(a.Opened).getTime()
       );
       const lastIncident = format(parseCustomDate(sortedIncidents[0].Opened), 'MMM d, yyyy');

      predictions.push({
        apiName,
        pastFailures,
        recentFailures,
        likelihood,
        lastIncident
      });
    });

         // Sort by likelihood and recent failures
     const sortedPredictions = predictions.sort((a, b) => {
       const likelihoodOrder = { 'High': 3, 'Medium': 2, 'Low': 1 };
       if (likelihoodOrder[a.likelihood] !== likelihoodOrder[b.likelihood]) {
         return likelihoodOrder[b.likelihood] - likelihoodOrder[a.likelihood];
       }
       return b.recentFailures - a.recentFailures;
     });
     
     console.log('Final API predictions:', sortedPredictions);
     return sortedPredictions;
  };

  // Extract API names with specific extensions
  const extractApiNameWithExtensions = (description: string): string | null => {
    const desc = description.toLowerCase();
    
    // Look for patterns with specific extensions
    const patterns = [
      /([a-zA-Z0-9-_]+)-api\b/,
      /([a-zA-Z0-9-_]+)-prc\b/,
      /([a-zA-Z0-9-_]+)-sys\b/,
      /([a-zA-Z0-9-_]+)-etl\b/,
      /([a-zA-Z0-9-_]+)-elt\b/,
      /([a-zA-Z0-9-_]+)-exp\b/,
      /([a-zA-Z0-9-_]+)-service\b/,
      /([a-zA-Z0-9-_]+)-app\b/,
    ];

    for (const pattern of patterns) {
      const match = desc.match(pattern);
      if (match) {
        return match[0]; // Return the full match (e.g., "batch-etl-sys")
      }
    }

    return null;
  };

  const getLikelihoodColor = (likelihood: string) => {
    switch (likelihood) {
      case 'High': return { bg: '#fee2e2', text: '#dc2626', border: '#fca5a5' };
      case 'Medium': return { bg: '#fef3c7', text: '#d97706', border: '#fcd34d' };
      default: return { bg: '#d1fae5', text: '#059669', border: '#86efac' };
    }
  };

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <Typography className="executive-title">Analyzing Data</Typography>
        </div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center">
          <Warning className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <Typography color="error" className="executive-title">Failed to analyze data</Typography>
        </div>
      </div>
    );
  }

  const { predictions, patterns, riskAssessment } = analysis;

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'increasing':
        return <TrendingUp className="h-6 w-6 text-red-500" />;
      case 'decreasing':
        return <TrendingDown className="h-6 w-6 text-green-500" />;
      default:
        return <TrendingFlat className="h-6 w-6 text-blue-500" />;
    }
  };

  const getOldRiskColor = (level: string) => {
    switch (level) {
      case 'High':
        return 'bg-red-100 text-red-700';
      case 'Medium':
        return 'bg-yellow-100 text-yellow-700';
      default:
        return 'bg-green-100 text-green-700';
    }
  };

  return (
    <div className="space-y-8">
      {/* Header Section */}
      {/* <div className="bg-gradient-to-r from-primary/10 to-primary/5 p-6 rounded-2xl">
        <div className="flex items-center justify-between">
          <div>
            <Typography className="text-2xl font-bold text-gray-800 mb-2">
              AI-Powered Analysis
            </Typography>
            <Typography className="text-gray-600">
              Real-time insights and predictions based on incident data
            </Typography>
          </div>
          <Timeline className="h-8 w-8 text-primary" />
        </div>
      </div> */}

      {/* API Failure Predictions Section */}
      <div className="space-y-6">
          <div className="bg-gradient-to-r from-red-50 to-orange-50 p-6 rounded-2xl">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Api className="h-8 w-8 text-red-600" />
                <div>
                  <Typography className="text-xl font-bold text-gray-800">
                    APIs That May Have Future Incidents
                  </Typography>
                
                </div>
              </div>
              <Chip 
                label={`${apiFailures.length} APIs at Risk`} 
                color="warning" 
                variant="outlined" 
              />
            </div>

            <div className="space-y-3">
              {apiFailures.map((api, index) => {
                const color = getLikelihoodColor(api.likelihood);
                return (
                  <div 
                    key={index}
                    className="flex items-center justify-between p-4 bg-white rounded-lg shadow-sm border-l-4"
                    style={{ borderLeftColor: color.text }}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <Typography variant="h6" className="font-semibold text-gray-800">
                          {api.apiName}
                        </Typography>
                        <Chip 
                          label={`${api.likelihood} Risk`}
                          size="small"
                          sx={{ 
                            backgroundColor: color.bg, 
                            color: color.text,
                            border: `1px solid ${color.border}`
                          }}
                        />
                      </div>
                      
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <Typography variant="caption" className="text-gray-600 block">
                            Total Past Failures
                          </Typography>
                          <Typography variant="body2" className="font-medium text-red-600">
                            {api.pastFailures}
                          </Typography>
                        </div>
                        <div>
                          <Typography variant="caption" className="text-gray-600 block">
                            Recent Failures (30d)
                          </Typography>
                          <Typography variant="body2" className="font-medium text-orange-600">
                            {api.recentFailures}
                          </Typography>
                        </div>
                        <div>
                          <Typography variant="caption" className="text-gray-600 block">
                            Last Incident
                          </Typography>
                          <Typography variant="body2" className="font-medium text-gray-700">
                            {api.lastIncident}
                          </Typography>
                        </div>
                      </div>
                    </div>
                    
                    <div className="ml-4">
                      {api.likelihood === 'High' && <Warning sx={{ color: '#dc2626', fontSize: 28 }} />}
                      {api.likelihood === 'Medium' && <BugReport sx={{ color: '#d97706', fontSize: 28 }} />}
                      {api.likelihood === 'Low' && <Security sx={{ color: '#059669', fontSize: 28 }} />}
                    </div>
                  </div>
                );
              })}
            </div>

            {apiFailures.length === 0 && (
              <div className="text-center py-8">
                <Security className="h-16 w-16 text-green-500 mx-auto mb-4" />
                <Typography className="text-gray-600">
                  No APIs with concerning failure patterns detected
                </Typography>
              </div>
                         )}
           </div>
         </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left Column - Predictions */}
        <div className="lg:col-span-4 space-y-8">
          <div className="executive-card bg-gradient-to-br from-white to-primary/5">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <Typography className="executive-title">Incident Predictions</Typography>
                <Timeline className="h-6 w-6 text-primary" />
              </div>
              
              <div className="bg-white/50 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-between mb-2">
                  <Typography className="text-4xl font-bold text-primary">
                    {predictions.nextWeekIncidents}
                  </Typography>
                  {getTrendIcon(predictions.trend)}
                </div>
                <Typography className="executive-label">
                  Expected incidents next week
                </Typography>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Typography className="executive-subtitle">Confidence Level</Typography>
                  <div className={`px-3 py-1 rounded-full ${predictions.confidence > 0.7 ? 'bg-green-100 text-green-700' : predictions.confidence > 0.4 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                    {(predictions.confidence * 100).toFixed(1)}%
                  </div>
                </div>
              </div>
            </CardContent>
          </div>
        </div>

        {/* Middle Column - Patterns */}
        <div className="lg:col-span-4 space-y-8">
          <div className="executive-card bg-gradient-to-br from-white to-primary/5">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <Typography className="executive-title">Pattern Analysis</Typography>
                <TrendingUp className="h-6 w-6 text-primary" />
              </div>

              <div className="space-y-6">
                <div className="bg-white/50 rounded-xl p-4">
                  <Typography className="executive-subtitle mb-3">
                    Common Root Causes
                  </Typography>
                  <div className="flex flex-wrap gap-2">
                    {patterns.commonRootCauses.map((cause, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm"
                      >
                        {cause}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="bg-white/50 rounded-xl p-4">
                  <Typography className="executive-subtitle mb-3">
                    Recurring Issues
                  </Typography>
                  <div className="flex flex-wrap gap-2">
                    {patterns.recurringIssues.map((issue, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm"
                      >
                        {issue}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </div>
        </div>

        {/* Right Column - Risk Assessment */}
        <div className="lg:col-span-4 space-y-8">
          <div className="executive-card bg-gradient-to-br from-white to-primary/5">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <Typography className="executive-title">Risk Assessment</Typography>
                <Security className="h-6 w-6 text-primary" />
              </div>

              <div className="space-y-6">
                <div className="bg-white/50 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-4">
                    <Typography className="executive-subtitle">Current Risk Level</Typography>
                    <span className={`px-4 py-2 rounded-full ${getOldRiskColor(riskAssessment.riskLevel)}`}>
                      {riskAssessment.riskLevel}
                    </span>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <Typography className="executive-subtitle mb-2">
                        Risk Factors
                      </Typography>
                      <div className="space-y-2">
                        {riskAssessment.factors.map((factor, index) => (
                          <div
                            key={index}
                            className="flex items-center space-x-2 text-sm text-gray-600"
                          >
                            <Warning className="h-4 w-4 text-primary" />
                            <span>{factor}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Typography className="executive-subtitle mb-2">
                        Recommendations
                      </Typography>
                      <div className="space-y-2">
                        {riskAssessment.recommendations.map((rec, index) => (
                          <div
                            key={index}
                            className="flex items-center space-x-2 text-sm text-gray-600"
                          >
                            <div className="h-4 w-4 rounded-full bg-primary/20 flex items-center justify-center">
                              <span className="text-primary text-xs">{index + 1}</span>
                            </div>
                            <span>{rec}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </div>
        </div>
      </div>
    </div>
  );
}; 